import { Component, Input, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { TranslateService } from '@ngx-translate/core';
import { EditionUpgradeAction } from '@vendasta/marketplace-apps';
import { RetailPrice } from '@vendasta/marketplace-packages';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { UpgradeCTADialogComponent, UpgradeCTADialogConfig } from '@vendasta/uikit';
import { VaFilteredMatTableComponent, VaFilteredMatTableService, VaTableSortService } from '@vendasta/va-filter2-table';
import { combineLatest, Observable } from 'rxjs';
import { filter, shareReplay, switchMap, take } from 'rxjs/operators';
import { UpgradeCTADialogService, UpgradeRedirectItem } from '../../upgrade-cta-dialog/upgrade-cta-dialog.service';
import { ListingSyncService } from '../listing-sync.service';
import { ListingsTableRowEvent } from './columns/column.directive';
import { ListingSidebarComponent } from './listing-sidebar/listing-sidebar.component';
import { ListingSyncTableFilterService } from './listing-sync-table-filter.service';
import { ListingSyncTableService } from './listing-sync-table.service';
import { APP_ID, SyncData } from '../../local-seo';

@Component({
  selector: 'local-seo-listing-sync-table',
  templateUrl: './listing-sync-table.component.html',
  styleUrls: ['./listing-sync-table.component.scss'],
  providers: [VaTableSortService, VaFilteredMatTableService],
  standalone: false,
})
export class ListingSyncTableComponent {
  @Input() isListingfromEmbedded = false;
  @ViewChild('table') table: VaFilteredMatTableComponent<ListingSyncTableService>;

  @Input() lspPurchaseDate: string;
  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild('listingSidebar') listingSidebar: ListingSidebarComponent;

  lspActive$: Observable<boolean>;
  dialogRef: MatDialogRef<UpgradeCTADialogComponent>;

  sidebarData: SyncData;

  constructor(
    public listingSyncTableService: ListingSyncTableService,
    private dialog: MatDialog,
    private upgradeDialogService: UpgradeCTADialogService,
    private listingSyncService: ListingSyncService,
    private translateService: TranslateService,
    private readonly analyticsService: ProductAnalyticsService,
    private filterService: ListingSyncTableFilterService,
    private vaFilteredMatTableService: VaFilteredMatTableService<any>,
  ) {
    this.lspActive$ = this.listingSyncService.lspActive$;
  }

  handleRowClick(event: SyncData): void {
    this.sidebarData = event;
    this.sidenav.open();
  }

  retry(): void {
    this.vaFilteredMatTableService.load();
    this.sidenav.close();
  }

  handleCellEvent(event: ListingsTableRowEvent): void {
    if (event.openUpgradeDialog) {
      this.OpenUpgradeDialog();
    }
  }

  OpenUpgradeDialog() {
    const editionChangeDetails$ = this.listingSyncService.addonID$.pipe(
      switchMap((addonID) => this.upgradeDialogService.getAddonUpgradeDetails$(addonID)),
    );
    const upgradePrice$: Observable<RetailPrice> = combineLatest([
      this.upgradeDialogService.partnerMarket$,
      this.listingSyncService.addonID$,
      editionChangeDetails$,
    ]).pipe(
      switchMap(([pmConfig, addonID, editionChangeDetails]) => {
        return this.upgradeDialogService.getUpgradePrice$(pmConfig, addonID, editionChangeDetails);
      }),
      shareReplay(1),
    );
    const formattedUpgradePrice$ = upgradePrice$.pipe(
      switchMap((upgradePrice) => this.upgradeDialogService.getFormattedRetailPrice$(upgradePrice)),
    );
    this.listingSyncService.addonID$
      .pipe(
        switchMap((addonID) =>
          combineLatest([
            upgradePrice$,
            formattedUpgradePrice$,
            editionChangeDetails$,
            this.upgradeDialogService.getCustomUpgradeModalContent$(addonID),
          ]).pipe(
            take(1),
            switchMap(([upgradePrice, formattedUpgradePrice, editionChangeDetails, customUpgradeModalContent]) => {
              const marketingPoints = [
                this.translateService.instant('LISTING_DISTRIBUTION.UPGRADE_MODAL.MARKETING_POINTS.LINE_1'),
                this.translateService.instant('LISTING_DISTRIBUTION.UPGRADE_MODAL.MARKETING_POINTS.LINE_2'),
                this.translateService.instant('LISTING_DISTRIBUTION.UPGRADE_MODAL.MARKETING_POINTS.LINE_3'),
                this.translateService.instant('LISTING_DISTRIBUTION.UPGRADE_MODAL.MARKETING_POINTS.LINE_4'),
                this.translateService.instant('LISTING_DISTRIBUTION.UPGRADE_MODAL.MARKETING_POINTS.LINE_5'),
              ];
              let ctaText =
                !!customUpgradeModalContent && customUpgradeModalContent.buttonText
                  ? customUpgradeModalContent.buttonText
                  : undefined;
              if (editionChangeDetails?.editionUpgradeAction && !ctaText) {
                switch (editionChangeDetails?.editionUpgradeAction) {
                  case EditionUpgradeAction.EDITION_UPGRADE_ACTION_CONTACT_SALES:
                    ctaText = this.translateService.instant('UPGRADE_DIALOG.PRICE.CONTACT_SALES');
                    break;
                  default:
                    ctaText = this.translateService.instant('LISTING_SYNC.LSP_ADDON.UPGRADE_MODAL.BUTTON_TEXT');
                    break;
                }
              }
              const dialogRef: UpgradeCTADialogConfig = {
                editionName: this.translateService.instant('LISTING_SYNC.LSP_ADDON.NAME'),
                imageText:
                  !!customUpgradeModalContent && customUpgradeModalContent.titleText
                    ? customUpgradeModalContent.titleText
                    : this.translateService.instant('LISTING_SYNC.LSP_ADDON.UPGRADE_MODAL.IMAGE_TEXT'),
                imageUrl:
                  !!customUpgradeModalContent && customUpgradeModalContent.imageUrl
                    ? customUpgradeModalContent.imageUrl
                    : `https://vstatic-prod.apigateway.co/listing-builder-client/assets/images/listing-sync-pro-upgrade-info.png`,
                keySellingPoints:
                  !!customUpgradeModalContent && customUpgradeModalContent.sellingPoints
                    ? customUpgradeModalContent.sellingPoints
                    : marketingPoints,
                ctaText: ctaText || this.translateService.instant('LISTING_SYNC.LSP_ADDON.UPGRADE_MODAL.BUTTON_TEXT'),
                activeOrderPrimaryText: this.translateService.instant(
                  'LISTING_SYNC.LSP_ADDON.UPGRADE_MODAL.ACTIVE_ORDER_PRIMARY',
                ),
                activeOrderSecondaryText: this.translateService.instant(
                  'LISTING_SYNC.LSP_ADDON.UPGRADE_MODAL.ACTIVE_ORDER_SECONDARY',
                ),
                activeOrderImageUrl: `https://vstatic-prod.apigateway.co/listing-builder-client/assets/images/active-order.png`,
                upgradeNowText: this.upgradeDialogService.getUpgradeText(upgradePrice, editionChangeDetails),
                price: formattedUpgradePrice,
                frequency: this.upgradeDialogService.getFrequencyText(upgradePrice),
              };

              this.dialogRef = this.dialog.open(UpgradeCTADialogComponent, {
                data: dialogRef,
              });
              return this.dialogRef.afterClosed().pipe(
                filter((upgradeClicked) => !!upgradeClicked),
                switchMap(() => {
                  const isCustomPackage =
                    editionChangeDetails.editionUpgradeAction ==
                    EditionUpgradeAction.EDITION_UPGRADE_ACTION_CUSTOM_ORDER;
                  const item: UpgradeRedirectItem = isCustomPackage
                    ? { packageId: editionChangeDetails.priceReferencePackageId }
                    : { appId: APP_ID, addonId: addonID };
                  return this.upgradeDialogService.buildBusinessCenterUpgradeRedirectInfo(
                    item,
                    editionChangeDetails.editionUpgradeAction,
                  );
                }),
              );
            }),
          ),
        ),
      )
      .subscribe((redirectInfo) => {
        this.upgradeDialogService.sendToVbc(redirectInfo);
      });
  }
  getSideNavTopGap(): number {
    return this.isListingfromEmbedded ? 124 : 100;
  }
}
